import { DocumentActionComponent, useDocumentOperation, useClient } from 'sanity'
import { useToast } from '@sanity/ui/toast'

/**
 * Custom publish action for `tireSize` documents.
 *
 * The readOnly `title` field is auto-populated from the tire format + dimensions +
 * the referenced wheel diameter (e.g. "255/40-18"), mirroring tireSize.preview.prepare.
 * It also enforces title uniqueness across tireSize documents.
 *
 * Restored from the original v2 action (sanity/schemas/actions/
 * setTireSizeTitleAndPublishAction.js) that was dropped in the Sanity v2 -> v4
 * migration — ported to v4 APIs (useClient/useDocumentOperation from 'sanity') and
 * fixing the original's illegal hook-call-inside-callback.
 */
export function createSetTireSizeTitleAndPublishAction(
  originalPublishAction: DocumentActionComponent,
): DocumentActionComponent {
  const SetTireSizeTitleAndPublishAction: DocumentActionComponent = (props) => {
    const { patch, publish } = useDocumentOperation(props.id, props.type)
    const client = useClient({ apiVersion: '2023-05-03' })
    const toast = useToast()
    const originalResult = originalPublishAction(props)

    // Only customise tireSize; everything else publishes normally.
    if (props.type !== 'tireSize') {
      return originalResult
    }

    if (!originalResult) {
      return null
    }

    return {
      ...originalResult,
      onHandle: async () => {
        const doc = (props.draft || props.published || {}) as Record<string, any>

        const v = (x: any) => (x === 0 || x ? x : '')

        const wheelDiameter = doc.diameter?._ref
          ? ((await client.fetch(
              `*[_type == "diameter" && _id == $ref][0].wheelDiameter`,
              { ref: doc.diameter._ref },
            )) ?? '')
          : ''

        let title = ''
        switch (doc.defaultTireFormat) {
          case 'standard':
            title = `${v(doc.treadWidthMetric)}/${v(doc.aspectRatio)}-${v(wheelDiameter)}`
            break
          case 'slick':
            title = `${v(doc.treadWidthMetric)}/${v(doc.tireDiameterMetric)}-${v(wheelDiameter)}`
            break
          case 'offroad':
            title = `${v(doc.tireDiameterImperial)}x${v(doc.treadWidthImperial)}R${v(wheelDiameter)}`
            break
          default:
            title = ''
        }

        // Enforce title uniqueness across tireSize documents (ignore self + drafts).
        if (title) {
          const duplicate = await client.fetch(
            `*[_type == "tireSize" && title == $title && !(_id in [$id, $draftId])][0]._id`,
            {
              title,
              id: props.id,
              draftId: `drafts.${props.id}`,
            },
          )

          if (duplicate) {
            toast.push({
              status: 'error',
              title: 'Publication failed',
              description: `A tire size with the title "${title}" already exists.`,
            })
            return
          }

          if (title !== doc.title) {
            patch.execute([{ set: { title } }])
            // Give the patch a tick to apply before publishing.
            await new Promise((resolve) => setTimeout(resolve, 100))
          }
        }

        publish.execute()

        if (originalResult.onHandle) {
          originalResult.onHandle()
        }
      },
    }
  }

  return SetTireSizeTitleAndPublishAction
}
